import ApiClient from "@/services/api/apiClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";

/** One entry of the institution dropdown of the add-teacher form. */
export type InstitutionOption = { value: string; label: string };

/** URL of the institution lookup. Single place to change if the backend moves it. */
export const INSTITUTION_OPTIONS_ENDPOINT = API_ENDPOINTS.INSTITUTIONS.LIST;

/** Max institutions requested (and listed): the dropdown does not paginate. */
export const INSTITUTION_OPTIONS_LIMIT = 100;

/** The ONLY query sent by the lookup: no tenant, status or search params. */
export const INSTITUTION_OPTIONS_PARAMS = {
  page_size: INSTITUTION_OPTIONS_LIMIT,
} as const;

const LIST_KEYS = ["institutions", "items", "data", "results"] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/** Finds the rows in a bare array, `{ institutions | items | data | results: [] }` or a `{ success, data }` envelope. */
function extractItems(body: unknown, depth = 0): unknown[] {
  if (Array.isArray(body)) return body;
  const rec = asRecord(body);
  if (!rec || depth > 2) return [];
  for (const key of LIST_KEYS) {
    if (Array.isArray(rec[key])) return rec[key] as unknown[];
  }
  for (const key of ["data", "result"]) {
    const nested = extractItems(rec[key], depth + 1);
    if (nested.length > 0) return nested;
  }
  return [];
}

/** Response body -> options labelled "name (code)"; rows without an id are skipped. */
export function mapInstitutionOptions(body: unknown): InstitutionOption[] {
  const options: InstitutionOption[] = [];
  for (const raw of extractItems(body)) {
    const item = asRecord(raw);
    if (!item) continue;
    const id = pickString(item.id);
    if (!id) continue;
    const name = pickString(item.institute_name);
    const code = pickString(item.institute_code);
    options.push({
      value: id,
      label: name ? (code ? `${name} (${code})` : name) : `Institution #${id}`,
    });
  }
  return options;
}

/** First INSTITUTION_OPTIONS_LIMIT institutions the backend returns for the caller. */
export async function fetchInstitutionOptions(): Promise<InstitutionOption[]> {
  const body: unknown = await ApiClient.get(INSTITUTION_OPTIONS_ENDPOINT, {
    ...INSTITUTION_OPTIONS_PARAMS,
  });
  if (asRecord(body)?.success === false) {
    const message = asRecord(body)?.message;
    throw new Error(
      typeof message === "string" && message
        ? message
        : "Failed to load institutions",
    );
  }
  return mapInstitutionOptions(body);
}
